// ═══════════════════════════════════════════════════════════════
// Handoff — New/Edit Handoff Note
// Ported from original Firebase React app — full feature parity
// Position selector, shift detection, template, drag-and-drop
// sections, signature, save draft / submit workflow
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Title, Text, Paper, Button, Group, TextInput,
  Select, SimpleGrid, Loader, Center, ActionIcon,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconArrowLeft, IconDeviceFloppy, IconSend, IconFileText, IconPlus,
  IconTrash, IconGripVertical, IconAlertCircle, IconUser, IconInfoCircle,
} from '@tabler/icons-react';
import RichTextarea from '../../components/ui/RichTextarea';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { DEFAULT_TEMPLATE_SECTIONS } from '../../types';
import type { ShiftType } from '../../types';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

let sectionIdCounter = 0;
const nextSectionId = () => `section-${++sectionIdCounter}`;

interface SectionItem {
  id: string;
  sectionTitle: string;
  content: string;
}

const makeDefaultSections = (): SectionItem[] =>
  DEFAULT_TEMPLATE_SECTIONS.map(s => ({
    id: nextSectionId(),
    sectionTitle: s.title,
    content: '',
  }));

function getCurrentShift(): ShiftType {
  const hour = new Date().getHours();
  return hour >= 6 && hour < 18 ? 'day' : 'night';
}
function getOppositeShift(shift: ShiftType): ShiftType {
  return shift === 'day' ? 'night' : 'day';
}
function getOperationalDate(): string {
  return new Date().toISOString().split('T')[0];
}

// Sortable section component for drag-and-drop
function SortableSection({ id, index, item, items, updateSection, removeSection }: {
  id: string; index: number; item: SectionItem; items: SectionItem[];
  updateSection: (i: number, field: string, value: string) => void;
  removeSection: (i: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper ref={setNodeRef} style={style} p="sm" mb="sm" withBorder>
      <Group mb="xs" gap="xs">
        <ActionIcon variant="subtle" color="#939598" size="sm" {...attributes} {...listeners}
          title="Drag to reorder" style={{ cursor: 'grab' }}>
          <IconGripVertical size={14} />
        </ActionIcon>
        <TextInput style={{ flex: 1 }} value={item.sectionTitle}
          onChange={(e) => updateSection(index, 'sectionTitle', e.target.value)}
          placeholder="Section title" size="sm" />
        {items.length > 1 && (
          <ActionIcon variant="subtle" color="var(--color-error)" size="sm" onClick={() => removeSection(index)} title="Remove section">
            <IconTrash size={14} />
          </ActionIcon>
        )}
      </Group>
      <RichTextarea
        value={item.content}
        onChange={(val) => updateSection(index, 'content', val)}
        placeholder="Enter handoff information for this section…"
        minRows={4}
      />
    </Paper>
  );
}

export default function HandoffNew() {
  const { eventId: paramEventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const navigate = useNavigate();

  const { user, activeIncident } = useAuthStore();
  const {
    events, positions, notes, loadAll, saveDraft, submitNote, createEvent,
  } = useHandoffStore();

  const [selectedEventId, setSelectedEventId] = useState<string>(paramEventId || '');
  const [autoCreating, setAutoCreating] = useState(false);
  const prevIncidentId = useRef<string | undefined>(undefined);
  const eventId = paramEventId || selectedEventId;
  const event = events.find(e => String(e.id) === eventId);

  // Auto-resolve from global active incident — re-runs when incident changes
  useEffect(() => {
    if (paramEventId || events.length === 0 || !activeIncident) return;
    const incId = String(activeIncident.id);
    const incidentChanged = incId !== prevIncidentId.current;
    prevIncidentId.current = incId;
    if (selectedEventId && !incidentChanged) return;
    const match = events.find(e =>
      (activeIncident.id && String(e.incidentId) === String(activeIncident.id))
      || e.eventName === activeIncident.name
    );
    if (match) {
      setSelectedEventId(String(match.id));
    } else if (!autoCreating) {
      // Auto-create handoff event from active incident
      setAutoCreating(true);
      createEvent({
        eventName: activeIncident.name,
        incidentId: activeIncident.id,
        eventType: activeIncident.incidentType || 'general',
        description: activeIncident.description || '',
      }).then(newId => {
        setSelectedEventId(String(newId));
        loadAll(); // reload to pick up new event
      }).finally(() => setAutoCreating(false));
    }
  }, [paramEventId, selectedEventId, events, activeIncident]);
  const { getPositionsForEvent } = useHandoffStore();
  const eventPositions = [...getPositionsForEvent(eventId)].sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));

  // Shift detection + manual override
  const detectedShift = getCurrentShift();
  const [selectedShift, setSelectedShift] = useState<ShiftType | null>(null);
  const currentShift = selectedShift || detectedShift;
  const oppositeShift = getOppositeShift(currentShift);
  const shiftDate = getOperationalDate();

  // Form state
  const [positionId, setPositionId] = useState('');
  const [positionName, setPositionName] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [items, setItems] = useState<SectionItem[]>(makeDefaultSections);
  const [senderSignature, setSenderSignature] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedNoteId, setSavedNoteId] = useState<number | null>(draftId ? Number(draftId) : null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [authorPersonId, setAuthorPersonId] = useState<string | null>(null);

  const { loadIncidents } = useAuthStore();

  // Load data
  useEffect(() => { loadAll(); loadIncidents(); }, [loadAll, loadIncidents]);

  // Resolve logged-in user's person record_id for authorId FK
  useEffect(() => {
    if (!user?.email || !TABLES.people || !FIELDS.people.email) return;
    queryRecords(TABLES.people, {
      filters: [{ field: FIELDS.people.email, operator: 'eq', value: user.email }],
      limit: 1,
    }).then(r => {
      if (r.records.length > 0) setAuthorPersonId(String(r.records[0].record_id));
    }).catch(() => {});
  }, [user?.email]);

  // Load existing draft if editing
  useEffect(() => {
    if (draftId) {
      const draft = notes.find(n => String(n.id) === draftId);
      if (draft) {
        setPositionId(String(draft.positionId) || '');
        setPositionName(draft.positionName || '');
        setRecipientName(draft.recipientName || '');
        setItems(
          draft.sections.length > 0
            ? draft.sections.map(s => ({ id: nextSectionId(), sectionTitle: s.title, content: s.content }))
            : makeDefaultSections()
        );
        setSenderSignature(draft.senderSignature || '');
        setSavedNoteId(Number(draftId));
      }
    }
  }, [draftId, notes]);

  // Auto-fill sender signature from user profile
  useEffect(() => {
    if (!senderSignature && user?.fullName) {
      setSenderSignature(user.fullName);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-determine recipient only when position or shift selection changes
  useEffect(() => {
    if (!positionId) { setRecipientName(''); return; }
    const pos = positions.find(p => String(p.id) === positionId);
    if (!pos) return;
    const recipName = oppositeShift === 'day' ? pos.dayUserName : pos.nightUserName;
    setRecipientName(recipName || '');
  }, [positionId, oppositeShift]); // eslint-disable-line react-hooks/exhaustive-deps

  // Position change handler
  const handlePositionChange = (posId: string | null) => {
    setPositionId(posId || '');
    const pos = eventPositions.find(p => String(p.id) === posId);
    setPositionName(pos?.positionName || '');
  };

  // Section handlers
  const updateSection = (index: number, field: string, value: string) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };
  const addSection = () => {
    setItems(prev => [...prev, { id: nextSectionId(), sectionTitle: '', content: '' }]);
  };
  const removeSection = (index: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Drag-and-drop
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = (event: { active: { id: string | number }; over: { id: string | number } | null }) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newItems = [...items];
    const [moved] = newItems.splice(oldIndex, 1);
    newItems.splice(newIndex, 0, moved);
    setItems(newItems);
  };

  // Build sections for API
  const buildSections = () => items.map(i => ({ title: i.sectionTitle, content: i.content }));

  // Save draft
  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveDraft({
        noteId: savedNoteId,
        eventId: eventId || '',
        positionId,
        positionName,
        fromShift: currentShift,
        toShift: oppositeShift,
        shiftDate,
        sections: buildSections(),
        senderName: user?.fullName || '',
        ...(authorPersonId ? { authorId: authorPersonId } : {}),
      });
      if (result.noteId && !savedNoteId) setSavedNoteId(result.noteId);
      setLastSaved(new Date());
      notifications.show({ title: 'Saved', message: 'Draft saved', color: 'var(--color-success)' });
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to save', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  // Submit handoff
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let noteId = savedNoteId;
      if (!noteId) {
        const result = await saveDraft({
          noteId: null,
          eventId: eventId || '',
          positionId,
          positionName,
          fromShift: currentShift,
          toShift: oppositeShift,
          shiftDate,
          sections: buildSections(),
          senderName: user?.fullName || '',
          ...(authorPersonId ? { authorId: authorPersonId } : {}),
        });
        noteId = result.noteId;
        setSavedNoteId(noteId);
      } else {
        await saveDraft({
          noteId,
          eventId: eventId || '',
          positionId,
          positionName,
          fromShift: currentShift,
          toShift: oppositeShift,
          shiftDate,
          sections: buildSections(),
          senderName: user?.fullName || '',
          ...(authorPersonId ? { authorId: authorPersonId } : {}),
        });
      }
      await submitNote(noteId, senderSignature);
      notifications.show({ title: 'Submitted', message: 'Handoff submitted!', color: 'var(--color-success)' });
      navigate(`/handoff/note/${noteId}`);
    } catch (err) {
      notifications.show({ title: 'Error', message: err instanceof Error ? err.message : 'Failed to submit', color: 'var(--color-error)' });
    } finally {
      setSubmitting(false);
      setConfirmSubmit(false);
    }
  };

  // Validation
  const hasContent = items.some(item => item.content?.trim().length > 0);
  const hasSections = items.some(item => item.sectionTitle?.trim().length > 0);
  const canSubmit = hasContent && hasSections && senderSignature.trim().length > 0 && !!positionId;

  if (!event) {
    return (
      <div>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/handoff/notes')} mb="sm">
          Back to Handoffs
        </Button>
        <Title order={3} mb="md" c="black">New Handoff Note</Title>
        {autoCreating ? (
          <Center py="md"><Loader size="sm" /><Text ml="sm" size="sm" c="dimmed">Setting up handoff for {activeIncident?.name}...</Text></Center>
        ) : (
          <Paper p="md" withBorder maw={500}>
            <Select
              label="Select Handoff Event"
              placeholder={events.length === 0 ? 'Loading events...' : 'Choose an event for this handoff...'}
              data={events.map(e => ({ value: String(e.id), label: e.eventName }))}
              value={selectedEventId || null}
              onChange={v => setSelectedEventId(v ?? '')}
              searchable
              size="sm"
            />
            {!activeIncident && events.length === 0 && (
              <Text size="xs" c="dimmed" mt="sm">
                <IconAlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Select an incident in the global nav bar to auto-create a handoff event.
              </Text>
            )}
            {events.length === 0 && (
              <Group mt="sm">
                <Button size="xs" variant="light" onClick={() => navigate('/handoff/events')}>
                  Manage Events
                </Button>
              </Group>
            )}
          </Paper>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
        onClick={() => paramEventId ? navigate(`/handoff/event/${paramEventId}`) : navigate('/handoff/notes')} mb="sm">
        {event.eventName}
      </Button>

      <Group justify="space-between" align="flex-start" mb="lg">
        <div>
          <Title order={3}>{draftId ? 'Edit Handoff Note' : 'New Handoff Note'}</Title>
          <Text c="dimmed" size="sm">
            {event.eventName}
            {lastSaved && <Text span c="var(--color-success)" size="xs" ml="sm">Draft saved</Text>}
          </Text>
        </div>
        <Group gap="xs">
          <Button variant="default" leftSection={<IconDeviceFloppy size={14} />}
            onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          {!confirmSubmit ? (
            <Button leftSection={<IconSend size={14} />} size="sm"
              onClick={() => setConfirmSubmit(true)}
              disabled={!canSubmit || submitting}
              title={!canSubmit ? 'Select a position, add content, and sign to submit' : ''}>
              Submit
            </Button>
          ) : (
            <Group gap="xs">
              <Button onClick={handleSubmit} disabled={submitting} size="sm">
                {submitting ? 'Submitting...' : 'Confirm Submit'}
              </Button>
              <Button variant="subtle" size="xs" onClick={() => setConfirmSubmit(false)}>Cancel</Button>
            </Group>
          )}
        </Group>
      </Group>

      {/* Metadata row */}
      <Paper p="md" withBorder mb="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {/* Position (required) */}
          <div>
            <Select label="Position" withAsterisk placeholder="Select position..."
              data={eventPositions.map(p => ({ value: String(p.id), label: p.positionName }))}
              value={positionId || null} onChange={handlePositionChange}
              searchable clearable />
            {!positionId && (
              <Text size="xs" c="#F86A0B" mt={4}>
                <IconAlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Position is required
              </Text>
            )}
            {eventPositions.length === 0 && (
              <Text size="xs" c="#F86A0B" mt={4}>
                <IconAlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                No positions found for this event.
                <Button variant="subtle" size="compact-xs" ml="xs"
                  onClick={() => navigate('/handoff/staff')}>
                  Set up in Shift Assignments
                </Button>
              </Text>
            )}
          </div>

          {/* Shift selector */}
          <div>
            <Text size="sm" fw={500} mb={4}>Your Shift</Text>
            <Button.Group>
              <Button variant={currentShift === 'day' ? 'filled' : 'outline'} size="sm"
                onClick={() => setSelectedShift('day')}>Day</Button>
              <Button variant={currentShift === 'night' ? 'filled' : 'outline'} size="sm"
                onClick={() => setSelectedShift('night')}>Night</Button>
            </Button.Group>
            <Text size="xs" c="dimmed" mt={4}>
              <IconInfoCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Handing off to <strong>{oppositeShift === 'day' ? 'Day' : 'Night'}</strong> shift
              {!selectedShift && <span> (auto-detected)</span>}
            </Text>
          </div>

          {/* Operational Date (auto-filled, read-only) */}
          <TextInput label="Operational Date" value={shiftDate} readOnly
            styles={{ input: { backgroundColor: '#F8F8F8', cursor: 'default' } }} />
        </SimpleGrid>

        {/* Recipient — auto-filled from position, editable as override */}
        {positionId && (
          <Paper p="sm" mt="md" style={{ background: 'rgba(75,63,255,0.08)' }}>
            <Group gap="xs" align="flex-end">
              <IconUser size={14} color="var(--blurple)" style={{ marginBottom: 8 }} />
              <TextInput
                label="Recipient"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Enter recipient name..."
                size="sm"
                style={{ flex: 1 }}
                description={recipientName
                  ? `${oppositeShift === 'day' ? 'Day' : 'Night'} shift for ${positionName}`
                  : `No one assigned to ${oppositeShift === 'day' ? 'Day' : 'Night'} shift — type a name`
                }
              />
            </Group>
          </Paper>
        )}
      </Paper>

      {/* Sections editor */}
      <Paper p="md" withBorder mb="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconFileText size={16} />
            <Title order={4}>Sections ({items.length})</Title>
          </Group>
          <Button variant="subtle" size="xs" leftSection={<IconPlus size={14} />} onClick={addSection}>
            Add Section
          </Button>
        </Group>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div>
              {items.map((item, index) => (
                <SortableSection key={item.id} id={item.id} index={index} item={item}
                  items={items} updateSection={updateSection} removeSection={removeSection} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </Paper>

      {/* Signature */}
      <Paper p="md" withBorder mb="md">
        <TextInput label="Sender Signature" withAsterisk value={senderSignature}
          onChange={(e) => setSenderSignature(e.target.value)}
          placeholder="Type your name as signature"
          styles={{ input: { fontStyle: 'italic' } }} />
        {!senderSignature.trim() && (
          <Text size="xs" c="#F86A0B" mt={4}>
            <IconAlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Signature is required to submit
          </Text>
        )}
      </Paper>

      {/* Bottom actions */}
      <Group justify="space-between" mt="lg">
        <Button variant="default" onClick={() => navigate(`/handoff/event/${eventId}`)}>Cancel</Button>
        <Group gap="xs">
          <Button variant="default" leftSection={<IconDeviceFloppy size={14} />}
            onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button leftSection={<IconSend size={14} />} size="sm"
            onClick={confirmSubmit ? handleSubmit : () => setConfirmSubmit(true)}
            disabled={!canSubmit || submitting}>
            {submitting ? 'Submitting...' : confirmSubmit ? 'Confirm Submit' : 'Submit'}
          </Button>
        </Group>
      </Group>
    </div>
  );
}
